/**
 * Environment configuration for the frontend application
 */

export interface EnvironmentConfig {
  apiBaseUrl: string;
  naviBaseUrl: string;
  ssoEnabled: boolean;
  ssoLoginUrl: string;
  debug: boolean;
  appName: string;
  dbProvider: 'cosmosdb' | 'mongodb'; // Add database provider selection
  azure: {
    storageAccountName: string;
    storageContainerName: string;
    blobBaseUrl: string;
  };
}

class Environment {
  private config: EnvironmentConfig;

  constructor() {
    // Get values from process.env (set by React at build time)
    const storageAccountName = process.env.REACT_APP_AZURE_STORAGE_ACCOUNT_NAME || '';
    const storageContainerName = process.env.REACT_APP_AZURE_STORAGE_CONTAINER_NAME || 'fes-documents';
    
    this.config = {
      apiBaseUrl: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001',
      naviBaseUrl: process.env.BACK_END_NAVI_BASE_URL || 'http://localhost:3000',
      ssoEnabled: process.env.REACT_APP_SSO_ENABLED === 'true',
      ssoLoginUrl: process.env.REACT_APP_SSO_LOGIN_URL || '',
      debug: process.env.REACT_APP_DEBUG === 'true',
      appName: process.env.REACT_APP_NAME || 'Navi Management Portal',
      dbProvider: (process.env.REACT_APP_DB_PROVIDER as 'cosmosdb' | 'mongodb') || 'cosmosdb',
      azure: {
        storageAccountName,
        storageContainerName,
        blobBaseUrl: storageAccountName 
          ? `https://${storageAccountName}.blob.core.windows.net/${storageContainerName}`
          : ''
      }
    };

    // Debug logging in development
    if (this.config.debug) {
      console.log('Environment Configuration:', {
        ...this.config,
        azure: {
          ...this.config.azure,
          blobBaseUrl: this.config.azure.blobBaseUrl ? '[CONFIGURED]' : '[NOT CONFIGURED]'
        }
      });
    }
  }

  get(): EnvironmentConfig {
    return this.config;
  }

  // Helper methods for common values
  getApiBaseUrl(): string {
    return this.config.apiBaseUrl;
  }

  getNaviBaseUrl(): string {
    return this.config.naviBaseUrl;
  }

  getAzureBlobBaseUrl(): string {
    return this.config.azure.blobBaseUrl;
  }

  getStorageAccountName(): string {
    return this.config.azure.storageAccountName;
  }

  getStorageContainerName(): string {
    return this.config.azure.storageContainerName;
  }

  isDebugEnabled(): boolean {
    return this.config.debug;
  }

  isAzureConfigured(): boolean {
    return !!(this.config.azure.storageAccountName && this.config.azure.storageContainerName);
  }

  getDbProvider(): 'cosmosdb' | 'mongodb' {
    return this.config.dbProvider;
  }

  isMongoDbEnabled(): boolean {
    return this.config.dbProvider === 'mongodb';
  }

  isCosmosDbEnabled(): boolean {
    return this.config.dbProvider === 'cosmosdb';
  }
}

// Export singleton instance
export const environment = new Environment();
export default environment;
